import { allFormFieldOptionValues } from "@/modules/forms/domain/lib/form-field-options";
import type { FormFieldRow } from "@/modules/forms/domain/repo/forms.repo";
import type { FormFieldOptionItem } from "@/server/platform/db/schema/forms";

export type FormAnswerFilterClause = {
  fieldId: string;
  value: string;
  mode: "exact" | "checkbox_contains";
};

/** ຖອງ URL search param `filt` (JSON ຂອງ Record<fieldId, optionValue>) */
export function parseFiltSearchParam(
  raw: string | undefined,
): Record<string, string> {
  if (!raw?.trim()) return {};
  try {
    const v = JSON.parse(raw.trim()) as unknown;
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    const out: Record<string, string> = {};
    for (const [k, val] of Object.entries(v)) {
      if (
        typeof k === "string" &&
        k.length > 0 &&
        typeof val === "string" &&
        val.trim().length > 0
      ) {
        out[k] = val.trim();
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** ກັບ undefined ຖ້າບໍ່ມີຕົວເລືອກໃດໆ */
export function serializeFiltSearchParam(
  filters: Record<string, string>,
): string | undefined {
  const entries = Object.entries(filters).filter(
    ([k, v]) => k.length > 0 && v.trim().length > 0,
  );
  if (entries.length === 0) return undefined;
  const obj = Object.fromEntries(entries.map(([k, v]) => [k, v.trim()]));
  return JSON.stringify(obj);
}

function fieldAllowsOther(f: FormFieldRow): boolean {
  const s = f.settings;
  return (
    typeof s === "object" &&
    s !== null &&
    "allowOther" in s &&
    (s as { allowOther?: boolean }).allowOther === true
  );
}

function optionValuesForField(f: FormFieldRow): string[] {
  return allFormFieldOptionValues(f.options as FormFieldOptionItem[] | null);
}

function isAllowedStoredValue(f: FormFieldRow, trimmed: string): boolean {
  if (trimmed.length > 512) return false;
  const values = optionValuesForField(f);
  if (values.includes(trimmed)) return true;
  if (fieldAllowsOther(f)) return trimmed.length > 0;
  return false;
}

/**
 * ກອງກຳນົດ filter ຈາກ URL ກັບກັບຟິວ choice / dropdown / checkbox ເທົ່ານັ້ນ
 */
export function buildAnswerFilterClauses(
  fields: FormFieldRow[],
  raw: Record<string, string>,
): FormAnswerFilterClause[] {
  const byId = new Map(fields.map((ff) => [ff.id, ff]));
  const out: FormAnswerFilterClause[] = [];
  for (const [fieldId, value] of Object.entries(raw)) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const f = byId.get(fieldId);
    if (
      !f ||
      (f.type !== "choice" && f.type !== "dropdown" && f.type !== "checkbox")
    ) {
      continue;
    }
    if (!isAllowedStoredValue(f, trimmed)) continue;
    const mode =
      f.type === "checkbox"
        ? ("checkbox_contains" as const)
        : ("exact" as const);
    out.push({ fieldId, value: trimmed, mode });
  }
  return out;
}
